import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import KitText from '../KitText';

export default function KitAmountInput({
  value,
  onChange,
  maxValue,
  minValue,
  autoFocus = false,
  underlineColor = '#212121',
  currency = 'USD',
  locale = 'en-US',
  symbol = '$',
}: {
  value: string;
  onChange: (value: string) => void;
  maxValue?: number;
  minValue?: number;
  autoFocus?: boolean;
  underlineColor?: string;
  currency?: string;
  locale?: string;
  symbol?: string;
}): React.ReactElement {
  const inputRef = useRef<TextInput>(null);

  const [amount, setAmount] = useState('0');

  const [isFocused, setIsFocused] = useState(false);

  const [fontSize, setFontSize] = useState(72);

  const [isSelected, setIsSelected] = useState(false);

  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const [currentAction, setCurrentAction] = useState('none');

  const [isPlaceholder, setIsPlaceholder] = useState(false);

  const [firstRender, setFirstRender] = useState(true);

  function handleChangeText(text: string) {
    let currentValue = text.replaceAll(',', '').replaceAll('$', '');
    let avoidZeroOverride = false;
    const cents = currentValue.split('.')[1] || null;
    const endWithPeriod = currentValue.endsWith('.');
    const allowedChars = /^[0-9.,]*$/;

    if (!currentValue) {
      currentValue = '0';
      avoidZeroOverride = true;
      setIsPlaceholder(true);
    } else {
      setIsPlaceholder(false);
    }

    if (!allowedChars.test(currentValue)) {
      return;
    }

    if ((cents?.length ?? 0) > 2) {
      return;
    }

    if (
      (isPlaceholder && selection.start === 0) ||
      (firstRender && selection.start === 0)
    ) {
      currentValue = currentValue.slice(0, -1);
      setFirstRender(false);
    } else if (isPlaceholder && selection.start === 1) {
      currentValue = currentValue.slice(1);
    }

    if (currentValue === '.') {
      currentValue = '0.';
    }

    const parsedValue = parseFloat(currentValue);

    if (
      (maxValue && parsedValue > maxValue) ||
      (minValue && parsedValue < minValue)
    ) {
      return;
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      maximumFractionDigits: cents ? cents.length : 0,
      currency,
    });

    let formattedValue = formatter.format(parsedValue);

    if (endWithPeriod && !cents) {
      formattedValue += '.';
    }

    formattedValue = formattedValue.replaceAll('$', '');

    if (formattedValue.replaceAll(',', '').replaceAll('.', '').length > 6) {
      setFontSize(60);
    } else {
      setFontSize(72);
    }

    if (isNaN(parsedValue)) {
      formattedValue = '0';
    }

    onChange(formattedValue);
  }

  useEffect(() => {
    if (value !== '0' && value !== '') {
      setIsPlaceholder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value || value === '') {
      setAmount('0');

      return;
    }

    const difference = value.length - amount.length;
    const newSelection = { start: 0, end: 0 };

    if (isSelected) {
      newSelection.start = selection.end - Math.abs(difference);
      newSelection.end = selection.end - Math.abs(difference);
    } else {
      if (currentAction === 'none') {
        newSelection.start = selection.start + Math.abs(difference);
        newSelection.end = selection.end + Math.abs(difference);
      } else if (currentAction === 'backspace') {
        newSelection.start = selection.start - Math.abs(difference);
        newSelection.end = selection.end - Math.abs(difference);
      } else if (currentAction === 'delete') {
        if (difference === -1) {
          newSelection.start = selection.start;
          newSelection.end = selection.end;
        } else if (difference === -2) {
          newSelection.start = selection.start - 1;
          newSelection.end = selection.end - 1;
        }
      }
    }

    if (value.length === 1) {
      newSelection.start = 1;
      newSelection.end = 1;
    }

    setImmediate(() => {
      setSelection(newSelection);
    });

    setFirstRender(false);

    setAmount(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function handleOnBlur() {
    setIsFocused(false);
    setIsSelected(false);

    const currentAmount = amount;

    if (currentAmount !== '0' && currentAmount.split('.')[1]?.length === 1) {
      setAmount(currentAmount + '0');
    } else if (currentAmount !== '0' && currentAmount.endsWith('.')) {
      setAmount(currentAmount + '00');
    } else if (currentAmount !== '0' && !currentAmount.split('.')[1]) {
      setAmount(currentAmount + '.00');
    }
  }

  function handleOnSelectionChange(event: any) {
    const { selection } = event.nativeEvent;

    setSelection(selection);

    if (selection.start !== selection.end) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }

  function handleOnKeyPress(event: any) {
    const { key } = event;

    if (key === 'Backspace') {
      setCurrentAction('backspace');
    } else if (key === 'Delete') {
      setCurrentAction('delete');
    } else {
      setCurrentAction('none');
    }
  }

  return (
    <View style={styles.mainContainer}>
      <View style={styles.amountContainer}>
        <KitText
          style={{ ...styles.symbolText, marginTop: fontSize === 72 ? 17 : 15 }}
        >
          {symbol}
        </KitText>
        <KitText
          style={{
            ...styles.amountText,
            borderBottomColor: isFocused ? underlineColor : Colors.N100,
            fontSize,
            lineHeight: fontSize,
          }}
        >
          {amount}
        </KitText>
      </View>
      <TextInput
        ref={inputRef}
        value={amount}
        onChangeText={handleChangeText}
        autoFocus={autoFocus}
        keyboardType='decimal-pad'
        onSelectionChange={handleOnSelectionChange}
        selection={selection}
        autoComplete='off'
        onKeyPress={handleOnKeyPress}
        style={{
          ...styles.textInput,
          fontSize,
          color: isSelected ? Colors.N900 : 'transparent',
          lineHeight: fontSize,
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={handleOnBlur}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    position: 'relative',
    marginBottom: Spacing.xl,
    maxWidth: 420,
  },
  textInput: {
    zIndex: 2,
    textAlign: 'center',
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as 'solid' | 'dotted' | 'dashed' | undefined,
    }),
    // @ts-ignore
    caretColor: Colors.N900,
    paddingLeft: 16,
    fontWeight: '600',
    fontFamily: 'inherit',
    paddingBottom: 2,
    letterSpacing: -3,
  },
  amountContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    flexDirection: 'row',
  },
  symbolText: {
    fontSize: 26,
    fontWeight: '600',
    alignSelf: 'flex-start',
    color: Colors.N900,
  },
  amountText: {
    borderBottomWidth: 2,
    textAlign: 'center',
    color: Colors.N900,
    fontWeight: '600',
    letterSpacing: -3,
  },
});
